import { html } from "haunted";
import { USA_CULTURE_CODE, BRITISH_CULTURE_CODE, COUNTRY_MAP } from "Services/constants";
import { DefaultRouteCountrySetting, DynamicSettings } from "./DynamicSettings";
import { useSaveDynamicSettings } from "./useSaveDynamicSettings";

export interface Props {
	newSettings: DynamicSettings;
	setNewSettings: (settings: DynamicSettings) => void;
}

const culturesWithoutPossibleCountries = [USA_CULTURE_CODE, BRITISH_CULTURE_CODE];

export const useRouteSettings = (props: Props) => {
	const saver = useSaveDynamicSettings({ newSettings: props.newSettings });

	const handleChange = (
		e: InputEvent,
		setting: DefaultRouteCountrySetting,
		field: keyof DefaultRouteCountrySetting
	) => {
		const value = (e.target as HTMLSelectElement).value;
		const oldSetting = props.newSettings.DefaultRouteCountrySettings.find(
			(s) => s.Culture.toLowerCase() === setting.Culture.toLowerCase()
		);
		const newRouteSettings = oldSetting
			? props.newSettings.DefaultRouteCountrySettings.map((s) =>
					s.Culture.toLowerCase() === oldSetting.Culture.toLowerCase() ? { ...oldSetting, [field]: value } : s
			  )
			: [...props.newSettings.DefaultRouteCountrySettings, { ...setting, [field]: value }];
		props.setNewSettings({
			...props.newSettings,
			DefaultRouteCountrySettings: newRouteSettings,
		});
	};

	const optionTemplate = (cultureCode: string, countryName: string, selectedValue: string) =>
		html`<option
			value=${cultureCode.substring(3)}
			?selected=${selectedValue && cultureCode.includes(selectedValue)}
		>
			${countryName}
		</option>`;

	const optionsTemplate = (selectedValue: string) =>
		html`<option value=""></option>
			${Array.from(COUNTRY_MAP.entries()).map(([cultureCode, countryName]) =>
				!culturesWithoutPossibleCountries.includes(cultureCode.toLowerCase())
					? optionTemplate(cultureCode, countryName, selectedValue)
					: ""
			)}`;

	const rowTemplate = ([cultureCode, countryName]: [string, string]) => {
		const setting = props.newSettings.DefaultRouteCountrySettings.find(
			(s) => s.Culture.toLowerCase() === cultureCode.toLowerCase()
		) || {
			Culture: cultureCode,
			DefaultOriginCountryCode: "",
			DefaultDestinationCountryCode: "",
		};

		return html`
			<tr class="border-b border-solid border-n-blue pb-4 odd:bg-gray-100">
				<td class="px-2 py-4">
					<div class="border-[#a6a6a6] p-[10px] text-right text-sm">${countryName}</div>
				</td>
				<td class="px-1 py-4">
					<select
						class="flex h-10 w-full appearance-none items-center rounded-lg border border-solid border-black px-4"
						@change=${(e: InputEvent) => handleChange(e, setting, "DefaultOriginCountryCode")}
					>
						${optionsTemplate(setting.DefaultOriginCountryCode)}
					</select>
				</td>
				<td class="px-1 py-4">
					<select
						class="flex h-10 w-full appearance-none items-center rounded-lg border border-solid border-black px-4"
						@change=${(e: InputEvent) => handleChange(e, setting, "DefaultDestinationCountryCode")}
					>
						${optionsTemplate(setting.DefaultDestinationCountryCode)}
					</select>
				</td>
			</tr>
		`;
	};

	const htmlTemplate = () => html`
		<section class="mt-5 max-w-[640px]">
			<table class="mb-4 w-full">
				<thead>
					<th class="px-2 py-4"></th>
					<th class="px-1 py-4">Default Origin Country</th>
					<th class="px-1 py-4">Default Destination Country</th>
				</thead>
				<tbody>
					${Array.from(COUNTRY_MAP.entries()).map(rowTemplate)}
				</tbody>
			</table>
			${saver.htmlTemplate()}
		</section>
	`;

	return { htmlTemplate };
};
